import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, Info, XCircle, X, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const levelIcons = {
  info: Info,
  warning: AlertTriangle,
  critical: XCircle,
};

const levelStyles = {
  info: 'bg-blue-50 border-blue-200 text-blue-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  critical: 'bg-red-50 border-red-200 text-red-800',
};

export const AppConfigProvider = ({ children, token }) => {
  const [appConfig, setAppConfig] = useState(null);
  const [dismissedBanners, setDismissedBanners] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dismissed_banners') || '[]'); } catch { return []; }
  });

  const fetchConfig = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/app/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppConfig(res.data);
    } catch (e) {
      console.error('Failed to fetch app config', e);
    }
  }, [token]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const dismissBanner = (bannerId) => {
    const next = [...dismissedBanners, bannerId];
    setDismissedBanners(next);
    sessionStorage.setItem('dismissed_banners', JSON.stringify(next));
  };

  // Maintenance mode screen
  if (appConfig?.maintenance?.enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="maintenance-screen">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-2xl bg-[hsl(var(--primary)/0.10)] flex items-center justify-center mx-auto mb-6">
            <Wrench className="w-10 h-10 text-[hsl(var(--primary))]" />
          </div>
          <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk' }}>
            Under Maintenance
          </h1>
          <p className="text-muted-foreground mb-6">
            {appConfig.maintenance.message || "We're performing scheduled maintenance. Please check back soon."}
          </p>
          <Button variant="outline" onClick={fetchConfig} data-testid="maintenance-retry-btn">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Active banners
  const activeBanners = (appConfig?.banners || []).filter(
    b => !dismissedBanners.includes(b.banner_id)
  );

  return (
    <>
      {/* Banner bar - fixed at top */}
      {activeBanners.length > 0 && (
        <div className="w-full z-[100] fixed top-0 left-0 right-0" data-testid="app-banners">
          {activeBanners.map(banner => {
            const Icon = levelIcons[banner.level] || Info;
            return (
              <div
                key={banner.banner_id}
                className={`flex items-center justify-between px-4 py-2.5 border-b text-sm ${levelStyles[banner.level] || levelStyles.info}`}
                data-testid={`app-banner-${banner.banner_id}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium">{banner.title}</span>
                  <span className="opacity-80">{banner.message}</span>
                </div>
                <button
                  onClick={() => dismissBanner(banner.banner_id)}
                  className="p-1 rounded hover:bg-black/5 flex-shrink-0"
                  data-testid={`dismiss-banner-${banner.banner_id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ paddingTop: activeBanners.length > 0 ? `${activeBanners.length * 43}px` : '0px' }}>
        {children}
      </div>
    </>
  );
};

export default AppConfigProvider;
